/** @flow */

import { takeLatest, put, all, call, select } from "redux-saga/effects";
import http from "@http";
import { push } from "connected-react-router";
import { route } from "@routes/helper";
import config from "@config";
import type { Action } from "redux-action";
import {
  GetReportingAppealsDefaultReports,
  GetReportingAppealsMyReports,
  GetReportingAppealsReport,
  GetReportingAppealsDefaultReport,
  GetAppealsFiltersSchema,
  CreateAppealsReport,
  UpdateAppealsReport,
  DeleteAppealsReport,
  ExportAppealsReport,
  ExportAppealsReportSuccess,
  GetAppealsReasons,
} from "@modules/reporting/constants/actions";
import actionCreators from "@modules/reporting/actionCreator";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import { getReportDataPage } from "@modules/reporting/selectors/reports/appeals";
import { downloadCsvFile } from "@sagas/utils";
import { parseSchema } from "@utils/schema";

type ReportRequest = $Exact<{
  clientId: number,
}>;

function* onGetAppealsFiltersSchema({ payload }: Action<ReportRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const schema = yield parseSchema("AppealsReportFiltersDTO", {
      clientId: payload.clientId,
    });

    yield put(actionCreators.GetAppealsFiltersSchemaSuccess(schema));
  } catch (e) {
    yield put(actionCreators.GetAppealsFiltersSchemaError(e.response));
  }
}

function* onGetAppealsReasons({ payload }: Action<ReportRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v1/reports/client/${payload.clientId}/reports/appeal-details/appeal-reasons`,
    );

    yield put(actionCreators.GetAppealsReasonsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetAppealsReasonsError(e.response));
  }
}

function* onGetReportingAppealsDefaultReports({
  payload,
}: Action<ReportRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v1/reports/client/${payload.clientId}/reports/appeal-details/default-reports`,
    );

    yield put(actionCreators.GetReportingAppealsDefaultReportsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetReportingAppealsDefaultReportsError(e.response));
  }
}

function* onGetReportingAppealsMyReports({
  payload,
}: Action<ReportRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v1/reports/client/${payload.clientId}/reports/appeal-details/my-saved-reports`,
    );

    yield put(actionCreators.GetReportingAppealsMyReportsSuccess(response.data));
    yield put(actionCreators.ReSetAppealsReportToSave());
  } catch (e) {
    yield put(actionCreators.GetReportingAppealsMyReportsError(e.response));
  }
}

type ReportDataRequest = $Exact<{
  clientId: number,
  report: Object,
  page?: number,
  noRedirect?: boolean,
}>;

function* onGetReportingAppealsReport({
  payload,
}: Action<ReportDataRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.report) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const currentPage = yield select(getReportDataPage);
    const page = (payload && payload.page) || currentPage;

    const [reportResponse, chartsResponse] = yield all([
      call(
        http.post,
        `/api/v1/reports/client/${payload.clientId}/reports/appeal-details`,
        payload.report.filters,
        {
          params: {
            page,
            limit: config.results50PerPage,
          },
        },
      ),
      call(
        http.post,
        `/api/v1/reports/client/${payload.clientId}/reports/appeal-details/charts`,
        payload.report.filters,
      ),
    ]);

    yield all([
      put(actionCreators.GetReportingAppealsReportSuccess(reportResponse.data)),
      put(actionCreators.GetReportingAppealsChartsSuccess(chartsResponse.data)),
    ]);

    if (!payload.noRedirect) {
      yield put(push(route("REPORTS_APPEALS_REPORT_PATH", { clientId: payload.clientId })));
    }
  } catch (e) {
    yield put(actionCreators.GetReportingAppealsReportError(e.response));
  }
}

type ReportingAppealsDefaultReportRequest = $Exact<{
  clientId: number,
  reportType: number,
  page?: number,
  noRedirect?: boolean,
}>;

function* onGetReportingAppealsDefaultReport({
  payload,
}: Action<ReportingAppealsDefaultReportRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.reportType) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const currentPage = yield select(getReportDataPage);
    const page = (payload && payload.page) || currentPage;
    const response = yield call(
      http.get,
      `/api/v1/reports/client/${payload.clientId}/reports/appeal-details/default-reports`,
    );
    const { data } = response;

    const [reportResponse, chartsResponse] = yield all([
      call(
        http.post,
        `/api/v1/reports/client/${payload.clientId}/reports/appeal-details`,
        data[payload.reportType].filters,
        {
          params: {
            page,
            limit: config.resultsPerPage,
          },
        },
      ),
      call(
        http.post,
        `/api/v1/reports/client/${payload.clientId}/reports/appeal-details/charts`,
        data[payload.reportType].filters,
      ),
    ]);

    yield all([
      put(actionCreators.GetReportingAppealsDefaultReportsSuccess(data)),
      put(actionCreators.GetReportingAppealsReportSuccess(reportResponse.data)),
      put(actionCreators.GetReportingAppealsChartsSuccess(chartsResponse.data)),
      put(actionCreators.GetReportingAppealsDefaultReportSuccess(data[payload.reportType])),
    ]);

    if (!payload.noRedirect) {
      yield put(push(route("REPORTS_APPEALS_REPORT_PATH", { clientId: payload.clientId })));
    }
  } catch (e) {
    yield all([
      put(actionCreators.GetReportingAppealsReportError(e.response)),
      put(actionCreators.GetReportingAppealsDefaultReportError(e.response)),
    ]);
  }
}

type CreateAppealsReportRequest = $Exact<{
  clientId: number,
  filters: Object,
  columns: string[],
  name: string,
}>;

export function* onCreateAppealsReport({
  payload,
}: Action<CreateAppealsReportRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.filters || !payload.columns || !payload.name) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.post,
      `/api/v1/reports/client/${payload.clientId}/reports/appeal-details/my-saved-reports`,
      {
        name: payload.name,
        filters: payload.filters,
        columns: payload.columns,
      },
    );

    yield all([
      put(actionCreators.CreateAppealsReportSuccess(response.data)),
      put(actionCreators.ToggleSaveReportModal({ visible: false })),
      put(actionCreators.GetReportingAppealsMyReports({ clientId: payload.clientId })),
      put(actionCreators.SetAppealsReportToSave({ ...response.data })),
      put(actionCreators.GetReportingAppealsDefaultReportSuccess({ ...response.data })),
      put(sendSuccessMessage("REPORT_CREATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.CreateAppealsReportError(e.response));
  }
}

type UpdateAppealsReportRequest = $Exact<{
  clientId: number,
  reportId: number,
  data: Object,
  shouldFetchReportData?: boolean,
}>;

export function* onUpdateAppealsReport({
  payload,
}: Action<UpdateAppealsReportRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.reportId || !payload.data) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.put,
      `/api/v1/reports/client/${payload.clientId}/reports/appeal-details/my-saved-reports/${payload.reportId}`,
      payload.data,
    );

    const additionalActions = payload.shouldFetchReportData
      ? [
          put(
            actionCreators.GetReportingAppealsReport({
              clientId: payload.clientId,
              report: response.data,
              noRedirect: true,
            }),
          ),
        ]
      : [];

    yield all([
      put(actionCreators.UpdateAppealsReportSuccess(response.data)),
      put(actionCreators.ToggleSaveReportModal({ visible: false })),
      put(actionCreators.GetReportingAppealsMyReports({ clientId: payload.clientId })),
      put(sendSuccessMessage("REPORT_UPDATED")),
      ...additionalActions,
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateAppealsReportError(e.response));
  }
}

type DeleteAppealsReportRequest = $Exact<{
  clientId: number,
  reportId: number,
  successMessage: string,
}>;

export function* onDeleteAppealsReport({
  payload,
}: Action<DeleteAppealsReportRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.reportId || !payload.successMessage) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    yield call(
      http.delete,
      `/api/v1/reports/client/${payload.clientId}/reports/appeal-details/my-saved-reports/${payload.reportId}`,
    );

    yield all([
      put(actionCreators.DeleteAppealsReportSuccess()),
      put(actionCreators.GetReportingAppealsMyReports({ clientId: payload.clientId })),
      put(sendSuccessMessage(payload.successMessage)),
    ]);
  } catch (e) {
    yield put(actionCreators.DeleteAppealsReportError(e.response));
  }
}

export function* onExportAppealsReport({ payload }: Action<*>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.filters || !payload.columns) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield http.post(
      `/api/v1/reports/client/${payload.clientId}/reports/appeal-details/export`,
      { filters: payload.filters, columns: payload.columns },
      {
        headers: { Accept: "text/csv" },
      },
    );

    yield put(actionCreators.ExportAppealsReportSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.ExportAppealsReportError(e.response));
  }
}

export function* onExportAppealsReportSuccess({ payload }: Action<any>): Generator<*, void, any> {
  yield downloadCsvFile(payload, "appeal-details-export.csv");
}

export default function* reportSaga(): Generator<*, void, void> {
  yield all([
    takeLatest(GetReportingAppealsDefaultReports, onGetReportingAppealsDefaultReports),
    takeLatest(GetReportingAppealsMyReports, onGetReportingAppealsMyReports),
    takeLatest(GetReportingAppealsReport, onGetReportingAppealsReport),
    takeLatest(GetReportingAppealsDefaultReport, onGetReportingAppealsDefaultReport),
    takeLatest(GetAppealsFiltersSchema, onGetAppealsFiltersSchema),
    takeLatest(GetAppealsReasons, onGetAppealsReasons),
    takeLatest(CreateAppealsReport, onCreateAppealsReport),
    takeLatest(UpdateAppealsReport, onUpdateAppealsReport),
    takeLatest(DeleteAppealsReport, onDeleteAppealsReport),
    takeLatest(ExportAppealsReport, onExportAppealsReport),
    takeLatest(ExportAppealsReportSuccess, onExportAppealsReportSuccess),
  ]);
}
